import { Injectable, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { BehaviorSubject, Subject, combineLatest, of } from 'rxjs';
import {
  catchError,
  distinctUntilKeyChanged,
  filter,
  retry,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class ConfigService implements OnDestroy {
  public pagination: any = {};
  public dashboard = new BehaviorSubject(null);

  private destroyed = new Subject();

  constructor(
    private firestore: AngularFirestore,
    private authService: AuthService,
  ) {
    this.authService.user
      .pipe(
        takeUntil(this.destroyed),
        filter((user) => !!user),
        distinctUntilKeyChanged('uid'),
        switchMap(() =>
          combineLatest([
            this.firestore.collection('config').doc('pagination').valueChanges(),
            this.firestore.collection('config').doc('dashboard').valueChanges(),
          ]).pipe(
            // only staff may read config. Right after the first login the token may not carry the
            // role claim yet -> retry a few times instead of ending the subscription for the session
            retry({ count: 5, delay: 5000 }),
            catchError(() => of([null, null])),
          ),
        ),
      )
      .subscribe(([pagination, dashboard]) => {
        this.pagination = pagination || {};
        this.dashboard.next(dashboard);
      });
  }

  getPagination(entity: string, userSpecific = true): number {
    const userData = this.authService.userData.value;
    if (!userData) {
      return 0;
    }
    if (userSpecific) {
      return userData.role === 'ADMIN'
        ? this.pagination[`${entity}`] || 0
        : userData[`${entity}`] || 0;
    }
    return this.pagination[`${entity}`] || 0;
  }

  ngOnDestroy() {
    this.destroyed.next(undefined);
    this.destroyed.complete();
  }
}
